import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, ArrowRight, ArrowUpRight, Check, ShoppingBag, PartyPopper, LogIn, LayoutDashboard, Users, Coins, Clock, Shirt, Gamepad2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ETSY_SHOP_URL, goToEtsy } from "@/lib/site";
import { track } from "@/lib/analytics";
import api from "@/lib/api";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1769812343385-8048c47d9667?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjB3ZWRkaW5nJTIwcmVjZXB0aW9uJTIwZWRpdG9yaWFsfGVufDB8fHx8MTc4NzgxMDkwNXww&ixlib=rb-4.1.0&q=85";
const WEDDING_FALLBACK = "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const BACH_FALLBACK = "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const onImgErr = (fb) => (e) => { if (e.currentTarget.src !== fb) e.currentTarget.src = fb; };

const WEDDING_FEATURES = [
  { title: "Smart budget", desc: "Every category, expense and deposit with a live remaining balance." },
  { title: "Guests & RSVP", desc: "Invitations, meals and dietary needs with live attending counts." },
  { title: "Vendors", desc: "Quotes, deposits and contracts for every supplier." },
  { title: "Seating", desc: "Drag-and-drop tables that just work." },
  { title: "Timeline & calendar", desc: "A guided checklist from twelve months out to the day." },
  { title: "The day & after", desc: "Run sheet, packing, music, gifts and the honeymoon." },
];

const BACH_FEATURES = [
  { icon: LayoutDashboard, title: "Dashboard", desc: "Countdown, budget and everything at a glance." },
  { icon: Users, title: "Guest List", desc: "RSVPs, contacts and dietary notes." },
  { icon: Coins, title: "Group Payments", desc: "Who owes what, collected and outstanding." },
  { icon: Clock, title: "Itinerary", desc: "Plan the weekend hour by hour." },
  { icon: Shirt, title: "Outfits", desc: "Looks for every event, from travel to the main night." },
  { icon: Gamepad2, title: "Games & more", desc: "Activities, packing, shopping and checklists." },
];

const MARQUEE = ["Budget", "Guest list", "RSVP", "Seating", "Vendors", "Timeline", "Itinerary", "Packing", "Music", "Gifts", "Honeymoon", "Theme board"];

function BrowserFrame({ src, alt, fb, className = "" }) {
  return (
    <div className={`rounded-2xl overflow-hidden border border-border shadow-[0_30px_80px_-30px_rgba(44,53,45,0.45)] bg-white ${className}`}>
      <div className="flex items-center gap-1.5 px-4 h-9 bg-secondary/60 border-b border-border">
        <span className="h-2.5 w-2.5 rounded-full bg-[#E5C0B8]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#E7D9B8]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#C9D6C4]" />
        <div className="ml-3 h-4 flex-1 max-w-[220px] rounded-full bg-background/70 text-[10px] text-muted-foreground flex items-center px-3 truncate">planwedding.com.au</div>
      </div>
      <img src={src} alt={alt} loading="lazy" onError={onImgErr(fb)} className="w-full block" />
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { authed } = useAuth();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const start = () => navigate(authed ? "/app" : "/login?mode=register");
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const exploreDemo = async () => {
    if (!authed) { navigate("/login?demo=1"); return; }
    try { await api.post("/demo"); toast.success("Demo planner loaded"); navigate("/app"); }
    catch { toast.error("Couldn't load the demo. Please try again."); }
  };

  return (
    <div className="min-h-screen bg-background grain relative">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/60">
        <div className="flex items-center justify-between px-6 sm:px-10 py-4 max-w-7xl mx-auto">
          <button onClick={() => scrollTo("top")} className="flex items-center gap-2.5" data-testid="nav-brand">
            <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center"><Heart className="h-4 w-4 text-primary-foreground" fill="currentColor" /></div>
            <span className="text-xl font-serif font-medium tracking-tight text-primary">PlanMyWedding</span>
          </button>
          <div className="flex items-center gap-4 sm:gap-6">
            <button onClick={() => scrollTo("wedding")} className="hidden md:inline text-sm text-foreground/80 link-underline" data-testid="nav-wedding">Wedding Planner</button>
            <button onClick={() => scrollTo("bachelorette")} className="hidden md:inline text-sm text-foreground/80 link-underline" data-testid="nav-bachelorette">Bachelorette Planner</button>
            <button onClick={() => scrollTo("how")} className="hidden md:inline text-sm text-foreground/80 link-underline" data-testid="nav-how">How It Works</button>
            <button onClick={() => navigate("/login")} className="text-sm text-foreground/80 link-underline inline-flex items-center gap-1.5" data-testid="nav-login"><LogIn className="h-3.5 w-3.5" /> Log In</button>
            <Button onClick={start} className="rounded-full transition-transform hover:scale-[1.03]" data-testid="nav-start">Start Planning</Button>
          </div>
        </div>
      </header>

      <span id="top" />

      {/* Hero — wedding focused */}
      <section ref={heroRef} className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 pt-10 sm:pt-16 pb-14">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-accent font-semibold mb-6">
              <span className="h-px w-8 bg-accent" /> Interactive online wedding planning
            </span>
            <h1 className="font-serif text-[clamp(2.75rem,8vw,7rem)] leading-[0.92] tracking-[-0.03em] text-primary">
              Your entire<br />
              wedding, <span className="italic font-light" style={{ fontVariationSettings: "'SOFT' 100, 'WONK' 1" }}>beautifully</span><br />
              planned.
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-md mt-8">
              A complete online platform you log into and use — budget, guests, vendors, timeline and every wedding-day detail in one calm, beautiful place. And now, a dedicated Bachelorette Planner too.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Button size="lg" onClick={() => scrollTo("wedding")} className="rounded-full text-base px-8 h-12 transition-transform hover:scale-[1.03]" data-testid="hero-explore-wedding">
                Explore Wedding Planner <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => scrollTo("bachelorette")} className="rounded-full text-base px-8 h-12 border-primary/20" data-testid="hero-explore-bachelorette">
                Explore Bachelorette Planner
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.2 }} className="lg:col-span-5">
            <div className="flex flex-col gap-2 text-sm text-foreground/70">
              {["No spreadsheets, ever", "The same planner on every device", "Your details stay private to you"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-accent shrink-0" /> {t}</span>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="relative mt-12 overflow-hidden rounded-[2rem]">
          <motion.img style={{ y: imgY }} src={HERO} alt="Elegant wedding reception table setting" className="w-full h-[320px] sm:h-[500px] object-cover will-change-transform" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
        </div>
      </section>

      {/* Marquee */}
      <div className="relative z-10 border-y border-border py-5 bg-primary text-primary-foreground overflow-hidden">
        <div className="marquee-track">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={`${m}-${i}`} className="mx-6 text-2xl font-serif italic font-light inline-flex items-center gap-6">{m} <span className="text-accent not-italic">✦</span></span>
          ))}
        </div>
      </div>

      {/* WEDDING PLANNER — flagship preview */}
      <section id="wedding" className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28 scroll-mt-20">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-10">
          <div className="lg:col-span-8">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-accent font-semibold mb-4">
              <span className="h-px w-8 bg-accent" /> Flagship product
            </span>
            <h2 className="font-serif text-5xl sm:text-7xl tracking-tight text-primary leading-[0.95]">Wedding Planner</h2>
          </div>
          <p className="lg:col-span-4 text-muted-foreground">The complete PlanMyWedding experience — a full interactive dashboard your customers log into and use on any device.</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}>
          <BrowserFrame src="/previews/wedding-dashboard.png" alt="Wedding Planner dashboard" fb={WEDDING_FALLBACK} />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <BrowserFrame src="/previews/wedding-budget.png" alt="Wedding Planner budget" fb={WEDDING_FALLBACK} />
          <BrowserFrame src="/previews/wedding-guests.png" alt="Wedding Planner guest list" fb={WEDDING_FALLBACK} />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 mt-14">
          {WEDDING_FEATURES.map((f) => (
            <div key={f.title} className="border-t border-border pt-4">
              <h3 className="font-serif text-xl text-primary">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mt-12">
          <Button size="lg" onClick={() => navigate("/demo/wedding")} className="rounded-full px-8 h-12 transition-transform hover:scale-[1.03]" data-testid="wedding-explore">Explore Demo <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          {ETSY_SHOP_URL && <Button size="lg" variant="outline" onClick={() => { track("demo_purchase_clicked", "wedding_planner"); goToEtsy(); }} className="rounded-full px-8 h-12 border-primary/20" data-testid="wedding-get"><ShoppingBag className="h-4 w-4 mr-2" /> Get Wedding Planner</Button>}
        </div>
      </section>

      {/* BACHELORETTE PLANNER — second product */}
      <section id="bachelorette" className="relative z-10 scroll-mt-20" style={{ background: "#FBF3F0" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-10">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] font-semibold mb-4" style={{ color: "#6B1D2F" }}>
                <span className="h-px w-8" style={{ background: "#6B1D2F" }} /> Additional planning product
              </span>
              <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-[0.96] inline-flex items-center gap-3" style={{ color: "#6B1D2F" }}>
                <PartyPopper className="h-8 w-8" /> Bachelorette Planner
              </h2>
              <p className="text-lg mt-5 max-w-xl" style={{ color: "#7A5A5C" }}>Everything you need to plan the celebration, all in one place — a separate planning space within PlanMyWedding.</p>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}>
            <BrowserFrame src="/previews/bach-dashboard.png" alt="Bachelorette Planner dashboard" fb={BACH_FALLBACK} />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <BrowserFrame src="/previews/bach-payments.png" alt="Bachelorette group payment tracker" fb={BACH_FALLBACK} />
            <BrowserFrame src="/previews/bach-itinerary.png" alt="Bachelorette itinerary" fb={BACH_FALLBACK} />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {BACH_FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl bg-white p-5 border" style={{ borderColor: "#EAD3CB" }}>
                <f.icon className="h-5 w-5 mb-3" style={{ color: "#6B1D2F" }} />
                <h3 className="font-serif text-lg" style={{ color: "#6B1D2F" }}>{f.title}</h3>
                <p className="text-sm mt-1 leading-relaxed" style={{ color: "#8A6A6C" }}>{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-12">
            <Button size="lg" onClick={() => navigate("/demo/bachelorette")} className="rounded-full px-8 h-12 text-white hover:opacity-90 transition-transform hover:scale-[1.03]" style={{ background: "#6B1D2F" }} data-testid="bach-explore">Explore Demo <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            {ETSY_SHOP_URL && <Button size="lg" variant="outline" onClick={() => { track("demo_purchase_clicked", "bachelorette_planner"); goToEtsy("bachelorette"); }} className="rounded-full px-8 h-12 bg-white hover:bg-white/70" style={{ borderColor: "#6B1D2F", color: "#6B1D2F" }} data-testid="bach-get"><ShoppingBag className="h-4 w-4 mr-2" /> Get Bachelorette Planner</Button>}
          </div>
        </div>
      </section>

      {/* CHOOSE YOUR PLANNER */}
      <section id="choose" className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 py-24 scroll-mt-20">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-accent font-semibold">
            <span className="h-px w-8 bg-accent" /> Choose your planner <span className="h-px w-8 bg-accent" />
          </span>
          <h2 className="font-serif text-4xl sm:text-6xl tracking-tight text-primary mt-5 leading-[0.98]">Two planners, one home.</h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto">Each is a separate one-time purchase on Etsy with its own private space. Own one, or both.</p>
        </div>
        <div className="grid lg:grid-cols-5 gap-6 items-stretch">
          {/* Wedding — flagship, wider */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="lg:col-span-3 rounded-[2rem] bg-primary text-primary-foreground p-8 sm:p-10 flex flex-col relative overflow-hidden">
            <span className="absolute right-6 top-6 text-[10px] uppercase tracking-[0.22em] rounded-full bg-accent/20 text-accent px-3 py-1">Flagship</span>
            <div className="flex items-center gap-3 mb-4"><div className="h-11 w-11 rounded-full bg-primary-foreground/15 flex items-center justify-center"><Heart className="h-5 w-5 text-accent" fill="currentColor" /></div>
              <h3 className="font-serif text-3xl">Wedding Planner</h3></div>
            <p className="text-primary-foreground/70 mb-6">The main, complete wedding planning experience.</p>
            <ul className="space-y-2.5 mb-8">
              {["Budget, guests & RSVP", "Seating, vendors & venues", "Timeline, calendar & itinerary", "Theme board, music, gifts & honeymoon"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-primary-foreground/90"><Check className="h-4 w-4 text-accent shrink-0 mt-0.5" /> {t}</li>
              ))}
            </ul>
            <p className="text-sm text-primary-foreground/60 mb-4">One-time purchase on Etsy → secure link to set up your planner.</p>
            <div className="mt-auto flex flex-wrap gap-3">
              {ETSY_SHOP_URL && <Button size="lg" variant="secondary" onClick={() => { track("demo_purchase_clicked", "wedding_planner"); goToEtsy(); }} className="rounded-full px-7 h-11" data-testid="choose-wedding-get"><ShoppingBag className="h-4 w-4 mr-2" /> Get Wedding Planner</Button>}
              <Button size="lg" variant="outline" onClick={() => navigate("/demo/wedding")} className="rounded-full px-7 h-11 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10" data-testid="choose-wedding-demo">Explore demo</Button>
            </div>
          </motion.div>

          {/* Bachelorette — secondary */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.06 }}
            className="lg:col-span-2 rounded-[2rem] p-8 sm:p-10 flex flex-col border" style={{ background: "#FBF3F0", borderColor: "#EAD3CB" }}>
            <div className="flex items-center gap-3 mb-4"><div className="h-11 w-11 rounded-full flex items-center justify-center" style={{ background: "#6B1D2F" }}><PartyPopper className="h-5 w-5 text-white" /></div>
              <h3 className="font-serif text-2xl" style={{ color: "#6B1D2F" }}>Bachelorette Planner</h3></div>
            <p className="mb-6" style={{ color: "#7A5A5C" }}>A dedicated planning space for the bachelorette celebration.</p>
            <ul className="space-y-2.5 mb-8">
              {["Guest list & group payments", "Itinerary, stays & activities", "Outfits, packing & shopping", "Games, checklist & notes"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm" style={{ color: "#7A5A5C" }}><Sparkles className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#6B1D2F" }} /> {t}</li>
              ))}
            </ul>
            <p className="text-sm mb-4" style={{ color: "#9A7A7C" }}>One-time purchase on Etsy → secure link to set up your planner.</p>
            <div className="mt-auto">
              {ETSY_SHOP_URL && <Button size="lg" onClick={() => { track("demo_purchase_clicked", "bachelorette_planner"); goToEtsy("bachelorette"); }} className="rounded-full px-7 h-11 text-white hover:opacity-90 w-full sm:w-auto" style={{ background: "#6B1D2F" }} data-testid="choose-bach-get"><ShoppingBag className="h-4 w-4 mr-2" /> Get Bachelorette Planner</Button>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative z-10 bg-secondary/30 border-y border-border scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-primary text-center">How it works</h2>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {[
              { n: "01", t: "Purchase on Etsy", d: "Buy the Wedding Planner, the Bachelorette Planner, or both from our Etsy shop." },
              { n: "02", t: "Get your secure link", d: "We instantly email a private link to create your account — Google or email & password." },
              { n: "03", t: "Log in & plan", d: "Open your interactive planner on any device. Everything saves and syncs automatically." },
            ].map((s) => (
              <div key={s.n} className="text-center">
                <div className="font-serif text-4xl text-accent">{s.n}</div>
                <h3 className="font-serif text-2xl text-primary mt-3">{s.t}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Existing customers */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 py-16 text-center">
        <p className="text-muted-foreground">Already have an account?</p>
        <Button variant="link" onClick={() => navigate("/login")} className="text-primary text-lg font-serif" data-testid="existing-login">Log in to your planner <ArrowUpRight className="h-4 w-4 ml-1" /></Button>
      </section>

      <footer className="relative z-10 border-t border-border py-10 text-center text-sm text-muted-foreground">
        <p className="font-serif italic text-base text-primary">PlanMyWedding</p>
        <p className="mt-1">Everything for your wedding, beautifully planned.</p>
      </footer>
    </div>
  );
}
